/**
 * Country and US state hub pages for the dark sky locations (ERI-2853).
 *
 * Search engines found few location pages because nothing but other location
 * pages linked to most of them. Every location now sits under a hub page for
 * its country, and US locations also under one for their state:
 *
 *   /dark-sky-locations/in/<country>
 *   /dark-sky-locations/in/united-states/<state>
 *
 * The list page, the sitemap, the build check and the hub pages all group
 * locations with these helpers, so they always agree. Imports stay relative:
 * the sitemap plugin and the build check load this file outside Vite.
 */
import { slugifyLocationName } from './locationSlug';

/** What the index and the hub pages need of a location. */
export interface IndexedLocation {
  slug: string;
  name: string;
  country: string;
  state_province: string | null;
  type: string;
  bortle_class?: number | null;
}

export interface PlaceLink {
  name: string;
  path: string;
}

export interface StateGroup extends PlaceLink {
  locations: IndexedLocation[];
}

export interface CountryGroup extends PlaceLink {
  /** Every location in the country, sorted. */
  locations: IndexedLocation[];
  /** US only: locations by state. */
  states: StateGroup[];
  /** US only: locations whose state_province is not a US state. */
  unplaced: IndexedLocation[];
}

export interface HubPage {
  kind: 'country' | 'state';
  /** The country or state as the data names it. */
  name: string;
  /** The country the hub is in, as the data names it. */
  country: string;
  path: string;
  /** The hubs above and including this one, for the breadcrumb. */
  trail: PlaceLink[];
  locations: IndexedLocation[];
}

export const LOCATIONS_PATH = '/dark-sky-locations';
export const HUB_PATH = `${LOCATIONS_PATH}/in`;
export const UNITED_STATES = 'United States';

export const US_STATES: readonly string[] = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];
const US_STATE_SET = new Set(US_STATES);

/** Countries that read with "the" in a sentence. */
const THE_COUNTRIES = new Set(['United States', 'United Kingdom', 'Netherlands', 'Czech Republic']);

/** Location types, singular and plural, as the intro counts them. */
const TYPE_LABELS: Record<string, [string, string]> = {
  park: ['park', 'parks'],
  reserve: ['reserve', 'reserves'],
  preserve: ['preserve', 'preserves'],
  sanctuary: ['sanctuary', 'sanctuaries'],
  community: ['community', 'communities'],
  urban_night_sky: ['urban night sky place', 'urban night sky places'],
  tourism_destination: ['tourism destination', 'tourism destinations'],
  trail: ['walking trail', 'walking trails'],
  camp: ['camp', 'camps'],
  observatory: ['observatory site', 'observatory sites'],
};
const OTHER_TYPE: [string, string] = ['other designated place', 'other designated places'];

/** Sorts the same way in Node at build time and in every browser, so hydration matches. */
function byKey<T>(key: (item: T) => string): (a: T, b: T) => number {
  return (a, b) => {
    const x = key(a);
    const y = key(b);
    return x < y ? -1 : x > y ? 1 : 0;
  };
}
const bySlug = byKey<IndexedLocation>((l) => l.slug);

export function placeSlug(name: string): string {
  return slugifyLocationName(name);
}

export function countryHubPath(country: string): string {
  return `${HUB_PATH}/${placeSlug(country)}`;
}

export function stateHubPath(state: string): string {
  return `${HUB_PATH}/${placeSlug(UNITED_STATES)}/${placeSlug(state)}`;
}

/** The US state a location is filed under, or null outside the US or when the state is not a US state. */
export function usState(location: Pick<IndexedLocation, 'country' | 'state_province'>): string | null {
  if (location.country !== UNITED_STATES || !location.state_province) return null;
  return US_STATE_SET.has(location.state_province) ? location.state_province : null;
}

/** "the United States", "Spain". */
export function countryLabel(country: string): string {
  return THE_COUNTRIES.has(country) ? `the ${country}` : country;
}

/** The hubs a location page sits under: its country, then its US state. */
export function placeTrail(location: Pick<IndexedLocation, 'country' | 'state_province'>): PlaceLink[] {
  if (!location.country) return [];
  const trail: PlaceLink[] = [{ name: location.country, path: countryHubPath(location.country) }];
  const state = usState(location);
  if (state) trail.push({ name: state, path: stateHubPath(state) });
  return trail;
}

/** Locations by country, sorted, with US locations also by state. */
export function groupByPlace(locations: IndexedLocation[]): CountryGroup[] {
  const countries = new Map<string, IndexedLocation[]>();
  for (const location of locations) {
    if (!location.country) continue;
    const list = countries.get(location.country) ?? [];
    list.push(location);
    countries.set(location.country, list);
  }
  return [...countries]
    .map(([name, list]): CountryGroup => {
      const sorted = [...list].sort(bySlug);
      const states = new Map<string, IndexedLocation[]>();
      const unplaced: IndexedLocation[] = [];
      if (name === UNITED_STATES) {
        for (const location of sorted) {
          const state = usState(location);
          if (!state) unplaced.push(location);
          else states.set(state, [...(states.get(state) ?? []), location]);
        }
      }
      return {
        name,
        path: countryHubPath(name),
        locations: sorted,
        states: [...states]
          .map(([state, members]) => ({ name: state, path: stateHubPath(state), locations: members }))
          .sort(byKey((s) => s.path)),
        unplaced,
      };
    })
    .sort(byKey((c) => c.path));
}

/** Every hub page's path. */
export function hubPaths(locations: IndexedLocation[]): string[] {
  return groupByPlace(locations).flatMap((country) => [country.path, ...country.states.map((s) => s.path)]);
}

/** The hub for a country slug, or a US state slug under the US; null when there is none. */
export function findHub(
  locations: IndexedLocation[],
  countrySlug: string | undefined,
  stateSlug?: string,
): HubPage | null {
  if (!countrySlug) return null;
  const country = groupByPlace(locations).find((c) => c.path === `${HUB_PATH}/${countrySlug}`);
  if (!country) return null;
  const countryLink = { name: country.name, path: country.path };
  if (stateSlug === undefined) {
    return { kind: 'country', name: country.name, country: country.name, path: country.path, trail: [countryLink], locations: country.locations };
  }
  const state = country.states.find((s) => s.path === `${country.path}/${stateSlug}`);
  if (!state) return null;
  return {
    kind: 'state',
    name: state.name,
    country: country.name,
    path: state.path,
    trail: [countryLink, { name: state.name, path: state.path }],
    locations: state.locations,
  };
}

function joinList(items: string[]): string {
  return items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function typeCounts(locations: IndexedLocation[]): Array<{ count: number; label: [string, string] }> {
  const counts = new Map<string, number>();
  for (const location of locations) {
    const key = TYPE_LABELS[location.type] ? location.type : 'other';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts]
    .map(([type, count]) => ({ count, label: TYPE_LABELS[type] ?? OTHER_TYPE }))
    .sort((a, b) => b.count - a.count || (a.label[0] < b.label[0] ? -1 : a.label[0] > b.label[0] ? 1 : 0));
}

/** "park", "urban night sky place": what a hub list says a location is. */
export function typeLabel(type: string): string {
  return (TYPE_LABELS[type] ?? OTHER_TYPE)[0];
}

/** "18 parks, 4 communities and 1 sanctuary". */
export function describeTypes(locations: IndexedLocation[]): string {
  return joinList(typeCounts(locations).map(({ count, label }) => `${count} ${count === 1 ? label[0] : label[1]}`));
}

/** "Utah", or "the United States". */
function hubPlace(hub: HubPage): string {
  return hub.kind === 'country' ? countryLabel(hub.name) : hub.name;
}

function places(count: number): string {
  return `${count} dark sky ${count === 1 ? 'place' : 'places'}`;
}

export function hubHeading(hub: HubPage): string {
  return `Dark Sky Locations in ${hubPlace(hub)}`;
}

export function hubTitle(hub: HubPage): string {
  const heading = hubHeading(hub);
  const branded = `${heading} | Milky Way Planner`;
  // Ahrefs flags titles over ~60 characters. The heading already names the
  // place; drop the brand when adding it would pass 60 (Taiwan is the one).
  return branded.length <= 60 ? branded : heading;
}

export function hubDescription(hub: HubPage): string {
  const count = hub.locations.length;
  return `${places(count)} in ${hubPlace(hub)} for Milky Way photography, ${count === 1 ? 'with its' : 'each with its'} Bortle class, best months and a free visibility calendar.`;
}

/** A short factual intro from the data: how many places, their designation types, their Bortle range. */
export function hubIntro(hub: HubPage): string {
  const count = hub.locations.length;
  const place = hubPlace(hub);
  let intro: string;
  if (count === 1) {
    const [label] = typeCounts(hub.locations)[0].label;
    intro = `Milky Way Planner has a page for ${places(1)} in ${place}, ${/^[aeiou]/i.test(label) ? 'an' : 'a'} ${label}.`;
  } else {
    intro = `Milky Way Planner has pages for ${places(count)} in ${place}: ${describeTypes(hub.locations)}.`;
  }
  const bortle = hub.locations
    .map((l) => l.bortle_class)
    .filter((b): b is number => typeof b === 'number');
  if (bortle.length > 0) {
    const min = Math.min(...bortle);
    const max = Math.max(...bortle);
    if (count === 1) intro += ` Its Bortle class is ${min}.`;
    else if (min === max && bortle.length === count) intro += ` Each has a Bortle class of ${min}.`;
    else if (min === max) intro += ` Their listed Bortle class is ${min}.`;
    else intro += ` Their Bortle classes run from ${min} to ${max}.`;
  }
  return intro;
}
